const { effect, computed, signal } = require('@node_modules/@preact/signals-core');

/** @typedef {import('./q-notification').Tab} Tab */
/** @typedef {import('./q-notification').Notification} Notification */
/** @typedef {import('./q-notification').InternalConfig} Config */

class QNotification {
    constructor(elementId, config) {
        this._element = $(`#${elementId}`);

        /** @type {Config}*/
        this._config = {
            icon: config.icon,
            showBadgeForTab: config.showBadgeForTab,
            title: config.title,
            clickWhenEmpty: config.clickWhenEmpty,
            hint: config.hint
        };

        this._notifications = this._mapNotificationData(config.tabs); 

        this._element.append(this._build());

        this._hasItems = computed(() => {
            return Boolean(Array.from(this._notifications).find(([id, tab]) => tab._size.value))
        });
    }

    _mapNotificationData(tabs) {
        /** @type {Map<string, Tab>}*/
        const map = new Map();

        tabs.forEach((tab) => {
            /** @type {Map<string, Notification>} */
            const notificationMap = new Map();

            tab.items.forEach((notification) => {
                this._insertNotificationInMap(notification, notificationMap);
            });
            
            map.set(`${tab.id}`, {
                ...tab,
                items: notificationMap,
                _size: signal(notificationMap.size),
                _uuid: uuidv4().replace(/-/g, '_')
            });
        });

        return map;
    };

    /**
     * 
     * @param {Notification} notification 
     * @param {Map<string, Notification} tabMap 
     */
    _insertNotificationInMap(notification, tabMap) {
        tabMap.set(`${notification.id}`, notification)
    }

    _build() {
        const button = this._createButton(this._config);
        const dropdown = this._createDropdown(this._config);

        this._element
            .addClass('q-notification')
            .empty()
            .append(button, dropdown);
    }

    _createButton() {
        const config = this._config;
        const button = $(/*html*/
            `<div class="topbar-item ml-2 mr-2" data-toggle="dropdown" data-offset="10px,0px" aria-expanded="false">
                <div class="btn btn-icon btn-clean btn-dropdown btn-md symbol">
                    <i class="text-white ${config.icon || 'las la-bell'} display-4 "></i>
                </div>
            </div>`
        );

        const badge = $(/*html*/`<i class="symbol-badge bg-primary border-0 d-none">`)
            .css({
                top: '8px',
                right: '7px',
                height: '10px',
                width: '10px'
            });

        button.find('.symbol').append(badge);


        if (config.clickWhenEmpty) {
            button.on('click', (e) => {
                if (this._hasItems.peek()) return;

                if (typeof config.clickWhenEmpty == 'string') window.open(config.clickWhenEmpty, '_blank');
                else if (typeof config.clickWhenEmpty == "function") {
                    config.clickWhenEmpty({
                        event: e,
                        component: this,
                    });
                }
            });

            this._element.on('show.bs.dropdown' , (e) => {
                if (!this._hasItems.peek()) e.preventDefault();
            });
        }

        if (config.showBadgeForTab) {
            const tabs = typeof config.showBadgeForTab == 'string' ? [config.showBadgeForTab] : config.showBadgeForTab;

            effect(() => {
                const tabWithItem = tabs.find((tab) => {
                    if (this._notifications.has(tab)) return this._notifications.get(tab)._size.value;
                });

                badge.toggleClass('d-none', !Boolean(tabWithItem));
            });
        }

        button
            .find('.btn')
            .tooltip({
                placement: 'bottom',
                title: config.hint || 'Notificações',
            });

        return button;
    }

    _createDropdown() {
        const config = this._config;
        const dropdown = $(/*html*/
            `<div class="dropdown-menu p-0 m-0 dropdown-menu-right dropdown-menu-anim-up dropdown-menu-lg" x-placement="top-end" style="width: 480px">
                <form>
                    <div class="d-flex flex-column pt-12 rounded-top bg-gray-100" style="color: var(--gray-200)">
                        <h4 class="d-flex flex-center rounded-top h2 font-weight-bolder text-dark">
                            <span>${config.title}</span>
                        </h4>
                    </div>
                </form>
            </div>`
        );

        dropdown
            .find('form div')
            .append(
                this._createTabsHeader(), 
                this._createTabsBody()
            );

        return dropdown;
    }

    _createTabsHeader() {
        const tabWrapper = $(/*html*/`<ul class="nav nav-bold nav-tabs nav-tabs-line nav-tabs-line-3x mt-3 p-0 center-full-width" role="tablist">`);

        tabWrapper.append(Array.from(this._notifications).map(([key, tab], index) => this._createTabLink(tab, !index)));
        
        return tabWrapper;
    }

    /**
     * @param {Tab} tab 
     * @param {boolean} isActive 
     */
    _createTabLink(tab, isActive) {
        const lengthEl = $(`<span class="label label-rounded label-primary ${tab.showSize ? '' : 'd-none'}">`);
        const tabLink = $(/*html*/
            `<li class="nav-item" style="">
                <a class="nav-link px-4 py-2 m-0 justify-content-center ${isActive ? 'show active' : ''}" data-toggle="tab" href="#tab_notification_${tab._uuid}">
                    ${tab.text}
                </a>
            </li>`
        );
        
        tabLink.find('a').append(lengthEl);

        if (tab.showSize) {
            effect(() => {
                const size = tab._size.value;
                const text = size > 99 ? '99+' : `${size}`;

                lengthEl
                    .text(text)
                    .toggleClass('d-none', !size)
                    .css('width', `${Math.max(text.length * 10, 20)}px`);

            });
        }

        return tabLink;
    }

    _createTabsBody() {
        const tabWrapper = $('<div class="tab-content">');

        tabWrapper.append(Array.from(this._notifications).map(([key, tab], index) => this._createTabContent(tab, !index)));

        return tabWrapper;
    }

    /**
     * @param {Tab} tab 
     * @param {boolean} isActive 
     */
    _createTabContent(tab, isActive) {
        const tabContent = $(/*html*/`<div class="tab-pane bg-white ${isActive ? 'show active' : ''}" id="tab_notification_${tab._uuid}" role="tabpanel">`);
        const scrollContent = $(/*html*/`<div class="custom-scrollbar p-2">`).css({
            height: '520px',
            'overflow-y': 'auto',
            'word-break': 'break-word',
        });

        tab._contentEl = tabContent;

        scrollContent.append(Array.from(tab.items).map(([key, item]) => this._createNotificationItem(item, { onClick: tab.itemClick })));

        return tabContent.append(scrollContent);
    }

    /**
     * @param {Notification} notification 
     * @param {{ onClick: Tab['itemClick'] }} options 
     */
    _createNotificationItem(notification, options) {
        const icon = {
            info: 'las la-info-circle',
            warning: 'las la-exclamation-triangle',
            success: 'las la-check',
            caq: 'q q-attention-center'
        };

        const backgroundIcon = {
            info: 'info',
            warning: 'warning',
            success: 'success',
            caq: 'primary'
        }

        const item = $(/*html*/`
            <div class="d-flex align-items-center mb-2 bg-gray-100 p-2 rounded-sm cursor-pointer">
                <div class="d-flex justify-content-center align-items-center mr-2 rounded-sm" style="min-width:24px; min-height:24px; background: var(--${backgroundIcon[notification.type] || 'white'})">
                    <i class="${icon[notification.type]} text-white"></i>
                </div>    
                <div class="d-flex flex-column flex-grow-1">
                    <span class="text-dark text-hover-primary font-size-h2 font-weight-bolder text-line-clamp-2">${notification.title}</span>
                    <span class="text-dark font-size-h3 text-line-clamp-2" style="max-width: 384px;">${notification.text}</span>
                </div>
                <div><i class="la la-angle-right font-size-h3 text-gray-700"></i></div>
            </div>
        `);

        notification._clearEl = () => {
            item.remove();
        };

        notification._element = item;

        if (typeof options.onClick == 'function') {
            item.on('click', (event) => {
                options.onClick({
                    notification,
                    event,
                    component: this
                });
            });
        }
        
        return item;
    }

    removeNotification(notificationId, tabId) {
        const id = `${notificationId}`;

        const remove = (/** @type {Tab} */tab) => {
            const notification = tab.items.get(id);

            notification._clearEl();

            tab.items.delete(id);

            tab._size.value = tab.items.size;
        }

        if (tabId) {
            const tab = this._notifications.get(tabId);

            if (!tab) return;

            remove(tab);
        }
        else {
            Array
                .from(this._notifications.values())
                .filter((tab) => tab.items.has(id))
                .forEach(remove);
        }
    }

    /**
     * @param {Notification} notification 
     * @param {string} tabId 
     */
    addNotification(notification, tabId, options = {}) {
        const tab = this._notifications.get(tabId);

        if (!tab) throw new Error(`Tab ${tabId} not found`);

        this._insertNotificationInMap(notification, tab.items);
        
        tab._size.value = tab.items.size;
        
        const newElement = this._createNotificationItem(notification, { onClick: tab.itemClick});
        
        if (typeof options.filterComp == 'function') {
            let insertIndex = 0;
            const sorted = Array
                .from(tab.items.values())
                .sort(options.filterComp);
            
            sorted.find((item, index) => (insertIndex = index, item.id == notification.id));

            const item = sorted[insertIndex - 1];
            
            if (item) {
                newElement.insertAfter(item._element);
            }
            else {
                tab._contentEl.find('.custom-scrollbar').prepend(newElement);
            }
        }
        else {
            tab._contentEl.find('.custom-scrollbar').prepend(newElement);
        }
    }

    /**
     * @param {string | number} id 
     * @param {string} tabId 
     */
    moveNotificationTo(notificationId, tabId, options = {}) {
        const id = `${notificationId}`;

        for (const tab of this._notifications.values()) {
            if (tab.items.has(id)) {
                const notification = tab.items.get(id);

                notification._clearEl();

                tab.items.delete(id);

                tab._size.value = tab.items.size;

                this.addNotification(notification, tabId, options);
            }
        }
    }

    /**
     * @param {string | number} notificationId 
     * @param {string} tabId 
     */
    getNotification(notificationId, tabId) {
        const id = `${notificationId}`;

        if (tabId) {
            const tab = this._notifications.get(tabId);

            if (!tab) return;

            return tab.items.get(id);
        }

        for (const tab of this._notifications.values()) {
            if (tab.items.has(id)) return tab.items.get(id);
        }
    }

    toggleDropdown(open = true) {
        this._element.find('[data-toggle="dropdown"]').dropdown(open ? 'show' : 'hide');
    }
}

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = QNotification;
}